import { InfaceAuthError } from '@inface-sdk/web-auth'

import Sentry from '../../config/sentry'

import { Feature } from '../common'
import { GameAuthService } from '../../services'

import { runFlow } from './utils'
import {
  validateParams, getWebToken, checkAccountStatus, issueGameToken,
} from './steps'
import { ERROR_CODES, ERROR_NAMES } from './constants'

import AccountStatusHandler from './AccountStatusHandler.class'

const modalService = {
  confirm: (...args) => window.$globalModal.confirm(...args),
  warning: (...args) => window.$globalModal.warning(...args),
}
export default class SSOForWebGame extends Feature {
  static SCHEME = 'insignsso://by-ticket/for-web-game'
  static MAIN_URL = 'https://signin.nexon.com'
  static SECURITY_URL = 'https://account-security.nexon.com'
  static GAME_RESTRICT_PATH = '/security/gamerestrict/mobile'

  constructor({
    env,
    mainUrl,
    infaceAuth,
    securityUrl,
  }) {
    super({ namespace: 'sso', enumKey: 'SSO.FOR_WEB_GAME' })

    const {
      // 공통 파라미터
      gid, // 진입 서비스의 gid (e.g. 넥슨플레이)
      type, // ingame-mobile
      locale,
      country,
      redirect_uri: redirectUri,

      // SSO For Web Game 전용 파라미터
      ticket,
      target_gid: targetGid, // 대상 게임의 gid (e.g. 메이플퍼즐)
      package_name: packageName,
    } = this.queryParamWithCookie

    this.gid = gid
    this.type = type
    this.locale = locale
    this.country = country
    this.redirectUri = redirectUri

    this.ticket = ticket
    this.targetGid = targetGid
    this.packageName = packageName

    this.$t = window.$i18next.t

    this.mainUrl = mainUrl || SSOForWebGame.MAIN_URL // Default main URL
    this.securityUrl = securityUrl || SSOForWebGame.SECURITY_URL // Default security URL
    this.gameRestrictPath = SSOForWebGame.GAME_RESTRICT_PATH // Default path for game restriction page

    this.infaceAuth = infaceAuth
    this.gameAuthService = new GameAuthService({
      gid: this.targetGid, env, locale, country,
    })

    this.accountStatusHandler = new AccountStatusHandler({
      modalService,
      navigate: (url) => this.navigateToUrlWithoutHistory(url),
      gameAuthService: this.gameAuthService,
      securityUrl: this.securityUrl,
      gameRestrictPath: this.gameRestrictPath,
      translate: this.$t,
      gid: this.targetGid,
    })

    console.debug('[SSOForWebGame Instance]', this)
  }

  /**
   * 웹 게임에 대한 SSO 연동을 처리합니다.
   * - 진입 서비스의 게임 토큰(A)으로 대상 웹 게임을 위한 게임 토큰(B)을 생성하는 것이 핵심입니다.
   * - 일부 계정 이상 상태 해제 후 다시 호출되는 경우, 웹 토큰은 로컬 변수에 캐싱된 값을 사용합니다.
   */
  async processTokenRoaming() {
    /**
     * NOTE.
     * web-auth에서 init을 호출하지 않을 경우 ToyTermsService의 boltApiUrl 환경이 정상적으로 반영되고 있지 않으며,
     * 환경 상관 없이 무조건 라이브 환경으로 호출하는 이슈가 있습니다.
     */
    if (process.env.NODE_ENV !== 'production') {
      const boltApiUrlSet = {
        local: 'https://dev-signin.nexon.com/toy/alpha',
        dev: 'https://dev-signin.nexon.com/toy/alpha',
        test: 'https://test-signin.nexon.com/toy',
      }
      const env = window.location.hostname.split('-')[0]
      const boltApiUrl = boltApiUrlSet[env]
      if (boltApiUrl) {
        this.infaceAuth.toyTermsService.boltApiUrl = boltApiUrl
      }
    }

    try {
      const context = {
        logger: Sentry,
        redirect: (url) => this.redirect(url),
        modalService,
        infaceAuth: this.infaceAuth,
        gameAuthService: this.gameAuthService,
        accountStatusHandler: this.accountStatusHandler,
        ticket: this.ticket,
        gid: this.gid,
        type: this.type,
        locale: this.locale,
        country: this.country,
        targetGid: this.targetGid,
        redirectUrl: this.redirectUri,
        securityUrl: this.securityUrl,
        gameRestrictPath: this.gameRestrictPath,
        webToken: null,
        packageName: this.packageName,
      }

      await runFlow(
        {
          steps: [
            validateParams,
            getWebToken,
            checkAccountStatus,
            issueGameToken,
            this.#redirect.bind(this),
          ],
          context,
        },
      )
    } catch (e) {
      console.error('[SSOForWebGame] processTokenRoaming error', e)
      const unexpectedError = new InfaceAuthError({
        code: ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_UNEXPECTED_ERROR,
        name: ERROR_NAMES[ERROR_CODES.FAIL_TO_GAME_TOKEN_ROAMING_FOR_UNEXPECTED_ERROR],
        data: e,
      })
      const err = e instanceof InfaceAuthError ? e : unexpectedError

      /**
       * 공통 에러 처리 부분이며 최종 에러 핸들링은 연동처에서 처리합니다.
       * insign-gw에서는 커스텀 스킴으로 에러 전송 후 로딩 화면을 노출시킵니다.
       */
      this.#sendErrorSignalToMobile({
        errorCode: err.code,
        errorName: err.name,
        errorMessage: err.message,
        rawError: err.data,
      })
    }
  }

  #redirect() {
    super.redirect(this.redirectUri)
  }

  #sendErrorSignalToMobile({
    errorCode, errorName, errorMessage, rawError,
  }) {
    const shouldLogError = rawError?.captureError !== false

    if (shouldLogError) {
      Sentry.withScope((scope) => {
        const error = new Error(`An error occurred in SSO For Web Game with error code: ${errorCode}`)
        error.name = `[${errorCode}] ${errorName}`
        error.message = errorMessage

        scope.setTag('error_code', errorCode)
        scope.setTag('error_name', errorName)
        scope.setContext('raw_error', rawError)

        Sentry.captureException(error)
      })
    }

    const responseData = {
      error_code: errorCode,
      error_name: errorName,
      error_message: errorMessage,
    }

    this.sendSignalToMobile(responseData)
  }

  #getErrorMessageForWebGame({ error_code: errorCode, error_message: errorMessage }) {
    const errorMessages = {
      113001: errorMessage || this.$t('error_message', { ns: 'common' }),
      113932: errorMessage || this.$t('error_message', { ns: 'common' }),
      113933: errorMessage || this.$t('error_message', { ns: 'common' }),
      113934: errorMessage || this.$t('error_message', { ns: 'common' }),
    }

    return errorMessages[errorCode] || this.$t('error_message', { ns: 'common' })
  }

  /**
   * 커스텀 스킴을 사용하여 모바일 앱으로 데이터를 전달합니다.
   * 커스텀 스킴 양식은 아래와 같습니다.
   * - e.g. {packageName}.insignsso://by-ticket/for-web-game?error_name=___
   */
  sendSignalToMobile(params) {
    const queryParams = new URLSearchParams(params)
    queryParams.set('error_message', this.#getErrorMessageForWebGame(params))
    const safeQuery = queryParams.toString().replace(/\+/g, '%20')
    const redirectUrl = `${this.packageName}.${SSOForWebGame.SCHEME}?${safeQuery}`

    window.location.href = redirectUrl
  }
}
